import React, { useEffect } from 'react';
import Select, { components as ReactSelectComponents } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import Icon from '../Icon/Icon';
import useSelectCustomizable from './useSelectCustomizable';

export type SelectCustomizableOption = {
  label: string;
  value: unknown;
  shortLabel?: string;
  __isNew__?: boolean;
};

type ActionMeta = { action: string; removedValue?: SelectCustomizableOption };

export type SelectCustomizableProps = {
  canCreate?: boolean;
  defaultValue?: unknown;
  error?: string;
  hideSelectedOptions?: boolean;
  iconTypeIndicator?: string;
  inputId?: string;
  isMulti?: boolean;
  isSearchable?: boolean;
  label?: React.ReactNode;
  menuPortalTarget?: HTMLElement | null;
  menuPosition?: 'fixed' | 'absolute';
  /** Cómo se muestran los valores del multi: 'inline' (pills de react-select)
   *  o 'sortedList' (lista <ul> de tags ordenada alfabéticamente). Solo aplica con isMulti. */
  multiValueDisplay?: 'inline' | 'sortedList';
  name?: string;
  noOptionsMessage?: string;
  onChange?: (value: unknown, actionMeta?: ActionMeta) => void;
  onCreateOption?: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  onMenuClose?: () => void;
  onMenuOpen?: () => void;
  options?: SelectCustomizableOption[];
  placeholder?: React.ReactNode;
  required?: boolean;
  /** Muestra los valores del multi como un resumen de una sola línea ("T1, T2, T3")
   *  en lugar de pills/lista. Solo aplica con isMulti. */
  showValuesAsText?: boolean;
  size?: 'default' | 'small';
  /** Tamaño de los tags en multiValueDisplay='sortedList'. */
  tagSize?: 'sm' | 'md';
  useShortLabels?: boolean;
  value?: unknown;
  /** 'default' = borde estándar; 'subtle' = borde tenue (divisor, sin sombra). */
  variant?: 'default' | 'subtle';
  // Props adicionales reenviadas a react-select (isDisabled, isLoading,
  // closeMenuOnSelect, components, aria-label, etc.)
  [key: string]: unknown;
};

// react-select v3 tiene tipados genéricos muy complejos; se castea a un
// componente laxo (mismo patrón que SelectCheckboxs con react-picky).
const SelectTyped = Select as unknown as React.ComponentType<Record<string, unknown>>;
const CreatableSelectTyped = CreatableSelect as unknown as React.ComponentType<Record<string, unknown>>;

// Componentes custom definidos a nivel de módulo para mantener identidad de referencia
// entre renders y evitar que react-select los desmonte/remonte en cada keystroke.
// Los datos del componente padre llegan a través de selectProps (react-select pasa
// todas las props desconocidas del <Select> en selectProps de cada custom component).

type ControlProps = {
  children?: React.ReactNode;
  innerProps?: Record<string, unknown>;
  menuIsOpen?: boolean;
};

function AccessibleControl({ children, innerProps, menuIsOpen, ...controlProps }: ControlProps): React.ReactElement {
  return (
    <ReactSelectComponents.Control innerProps={{ ...innerProps }} menuIsOpen={menuIsOpen} {...controlProps}>
      {children}
    </ReactSelectComponents.Control>
  );
}

type SelectPropsBag = {
  menuIsOpen?: boolean;
  _listboxId?: string;
  _focusedOptionId?: string;
  _onOptionFocusChange?: (id: string) => void;
  _errorId?: string;
  _hasError?: boolean;
  _required?: boolean;
  _removeLabel?: string;
  showValuesAsList?: boolean;
  showValuesAsText?: boolean;
  useShortLabels?: boolean;
  iconTypeIndicator?: string;
  onChange?: (value: unknown, actionMeta?: ActionMeta) => void;
};

function AccessibleInput({ selectProps, ...inputProps }: { selectProps: SelectPropsBag }): React.ReactElement {
  return (
    <ReactSelectComponents.Input
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={selectProps.menuIsOpen ? 'true' : 'false'}
      aria-controls={selectProps._listboxId}
      aria-activedescendant={selectProps._focusedOptionId || undefined}
      aria-invalid={selectProps._hasError ? 'true' : undefined}
      aria-describedby={selectProps._hasError ? selectProps._errorId : undefined}
      aria-required={selectProps._required ? 'true' : undefined}
      selectProps={selectProps}
      {...inputProps}
    />
  );
}

type MenuProps = {
  children?: React.ReactNode;
  innerProps?: Record<string, unknown>;
  selectProps: SelectPropsBag;
};

function AccessibleMenu({ children, innerProps, selectProps, ...menuProps }: MenuProps): React.ReactElement {
  return (
    <ReactSelectComponents.Menu
      innerProps={{ ...innerProps, role: 'listbox', id: selectProps._listboxId }}
      selectProps={selectProps}
      {...menuProps}
    >
      {children}
    </ReactSelectComponents.Menu>
  );
}

type OptionProps = {
  children?: React.ReactNode;
  innerProps?: { id?: string } & Record<string, unknown>;
  isSelected?: boolean;
  isFocused?: boolean;
  selectProps: SelectPropsBag;
};

function AccessibleOption({ children, innerProps, isSelected, isFocused, selectProps, ...optionProps }: OptionProps): React.ReactElement {
  useEffect(() => {
    if (isFocused && innerProps?.id) {
      selectProps._onOptionFocusChange?.(innerProps.id);
    }
  }, [isFocused, innerProps?.id, selectProps._onOptionFocusChange]);

  return (
    <ReactSelectComponents.Option
      innerProps={{ ...innerProps, role: 'option', 'aria-selected': isSelected }}
      isSelected={isSelected}
      isFocused={isFocused}
      selectProps={selectProps}
      {...optionProps}
    >
      {children}
    </ReactSelectComponents.Option>
  );
}

function DropdownIndicator({ innerProps, selectProps }: { innerProps?: Record<string, unknown>; selectProps: SelectPropsBag }): React.ReactElement {
  return (
    <div className="select-customizable__indicator " {...innerProps}>
      {selectProps.iconTypeIndicator ? (
        <Icon name={selectProps.iconTypeIndicator} size="sm" color="black" />
      ) : (
        <div className="select-customizable__dropdown-chevron" />
      )}
    </div>
  );
}

function MultiValueContainer({ selectProps }: { selectProps: SelectPropsBag }): React.ReactElement | null {
  return selectProps.showValuesAsList || selectProps.showValuesAsText ? null : (undefined as unknown as React.ReactElement);
}

function sortByDisplayLabel(values: SelectCustomizableOption[], useShortLabels?: boolean): SelectCustomizableOption[] {
  return [...values].sort((a, b) => {
    const la = useShortLabels && a.shortLabel ? a.shortLabel : a.label;
    const lb = useShortLabels && b.shortLabel ? b.shortLabel : b.label;
    return la.localeCompare(lb);
  });
}

type InlineValueChipProps = {
  option: SelectCustomizableOption;
  displayLabel: string;
  removeLabel: string;
  onRemove: (option: SelectCustomizableOption) => void;
};

function InlineValueChip({ option, displayLabel, removeLabel, onRemove }: InlineValueChipProps): React.ReactElement {
  function handleClick(e: React.MouseEvent): void {
    e.stopPropagation();
    onRemove(option);
  }
  return (
    <li className="select-customizable__inline-value" aria-label={option.label}>
      <span className="select-customizable__inline-value-label">{displayLabel}</span>
      <button
        className="select-customizable__inline-value-remove"
        onClick={handleClick}
        type="button"
        aria-label={`${removeLabel} ${option.label}`}
      />
    </li>
  );
}

type ValueContainerProps = {
  children?: React.ReactNode;
  hasValue?: boolean;
  getValue?: () => SelectCustomizableOption[];
  selectProps: SelectPropsBag;
};

// showValuesAsText: resumen de una sola línea ("T1, T2, T3"), sin control para quitar
// valores individuales desde el propio control (se gestiona abriendo el desplegable).
function ValueSummary({ children, hasValue, getValue, selectProps }: ValueContainerProps): React.ReactElement {
  if (!hasValue) {
    return <div className="select-customizable__value-container select-customizable__value-container--chips">{children}</div>;
  }

  const sorted = sortByDisplayLabel(getValue?.() ?? [], selectProps.useShortLabels);
  const summary = sorted.map((v) => (selectProps.useShortLabels && v.shortLabel ? v.shortLabel : v.label)).join(', ');

  return (
    <div className="select-customizable__value-container select-customizable__value-container--chips">
      <span className="select-customizable__value-summary" title={summary}>
        {summary}
      </span>
      {Array.isArray(children) ? children[1] : null}
    </div>
  );
}

function ValueContainer({ children, hasValue, getValue, selectProps }: ValueContainerProps): React.ReactElement {
  if (selectProps.showValuesAsText) {
    return (
      <ValueSummary hasValue={hasValue} getValue={getValue} selectProps={selectProps}>
        {children}
      </ValueSummary>
    );
  }

  if (!selectProps.showValuesAsList) {
    return children as React.ReactElement;
  }

  if (!hasValue) {
    return (
      <div className="select-customizable__value-container select-customizable__value-container--chips">
        {children}
      </div>
    );
  }

  const values = getValue?.() ?? [];
  const sorted = sortByDisplayLabel(values, selectProps.useShortLabels);

  function handleRemoveValue(v: SelectCustomizableOption): void {
    const newValues = values.filter((val) => val.value !== v.value);
    selectProps.onChange?.(newValues, { action: 'remove-value', removedValue: v });
  }

  return (
    <div className="select-customizable__value-container select-customizable__value-container--chips">
      <ul className="select-customizable__inline-values">
        {sorted.map((v) => {
          const displayLabel = selectProps.useShortLabels && v.shortLabel ? v.shortLabel : v.label;
          return (
            <InlineValueChip
              key={String(v.value)}
              option={v}
              displayLabel={displayLabel}
              removeLabel={selectProps._removeLabel ?? 'Remove'}
              onRemove={handleRemoveValue}
            />
          );
        })}
      </ul>
      {Array.isArray(children) ? children[1] : null}
    </div>
  );
}

export const utilsSelectCustomizable = {
  /**
   * Devuelve las opciones que necesita SelectCustomizable, recorriendo el array
   * elements y accediendo a propertyToLabel para el label y a propertyToValue
   * para el value de cada opción.
   */
  getOptionsProperties(
    elements: Record<string, unknown>[],
    propertyToLabel: string,
    propertyToValue: string
  ): { label: unknown; value: unknown }[] {
    return elements && propertyToLabel && propertyToValue
      ? elements.map((element) => ({
          label: element[propertyToLabel],
          value: element[propertyToValue],
        }))
      : [];
  },
};

/**
 * SelectCustomizable — Select accesible (WCAG AA) basado en react-select, con
 * label/error/required internos, modo creatable, multi con valores en lista
 * ordenada (multiValueDisplay='sortedList'), grupos y soporte de teclado completo.
 */
function SelectCustomizable({
  canCreate,
  defaultValue,
  error,
  hideSelectedOptions = false,
  iconTypeIndicator,
  inputId,
  isMulti = false,
  isSearchable,
  label,
  menuPortalTarget,
  menuPosition = 'fixed',
  multiValueDisplay = 'inline',
  name,
  noOptionsMessage,
  onChange = () => null,
  onCreateOption,
  onKeyDown,
  onMenuClose,
  onMenuOpen,
  options = [],
  placeholder,
  required,
  showValuesAsText = false,
  size = 'default',
  tagSize = 'md',
  useShortLabels,
  value,
  variant = 'default',
  'aria-label': ariaLabel,
  ...props
}: Readonly<SelectCustomizableProps>): React.ReactElement {
  const {
    resolvedInputId,
    labelId,
    errorId,
    selectRef,
    finalIsSearchable,
    finalMenuPlacement,
    getNoOptionsMessage,
    getCreateLabel,
    getScreenReaderStatus,
    sharedStyles,
    selectStyles,
    resolvedPlaceholder,
    resolvedAriaLabel,
    labelTitle,
    className,
    isSortedList,
    sharedProps,
    handleCreatableChange,
    formatGroupLabel,
    getOptionLabel,
  } = useSelectCustomizable({
    inputId,
    name,
    isSearchable,
    error,
    noOptionsMessage,
    label,
    ariaLabel: ariaLabel as string | undefined,
    placeholder,
    required,
    variant,
    size,
    tagSize,
    iconTypeIndicator,
    multiValueDisplay,
    showValuesAsText,
    useShortLabels,
    onChange,
    onCreateOption,
    onKeyDown,
    onMenuOpen,
    onMenuClose,
  });

  return (
    <>
      {label && (
        <label className="select-customizable__label" title={labelTitle} id={labelId}>
          {label} {required && <sup aria-hidden="true">*</sup>}
        </label>
      )}
      {canCreate ? (
        <CreatableSelectTyped
          ref={selectRef}
          menuPosition="fixed"
          menuPlacement={finalMenuPlacement}
          options={options}
          className={className}
          classNamePrefix="select-customizable"
          defaultValue={defaultValue}
          value={value}
          placeholder={resolvedPlaceholder}
          inputId={resolvedInputId}
          aria-labelledby={label ? labelId : undefined}
          aria-label={resolvedAriaLabel}
          components={{
            DropdownIndicator,
            Control: AccessibleControl,
            Input: AccessibleInput,
            Menu: AccessibleMenu,
            Option: AccessibleOption,
          }}
          noOptionsMessage={getNoOptionsMessage}
          screenReaderStatus={getScreenReaderStatus}
          isMulti={isMulti}
          onChange={handleCreatableChange}
          onCreateOption={onCreateOption}
          createOptionPosition="last"
          isClearable
          formatCreateLabel={getCreateLabel}
          name={name}
          styles={sharedStyles}
          {...props}
          {...sharedProps}
        />
      ) : (
        <SelectTyped
          ref={selectRef}
          menuPosition={menuPosition}
          menuPlacement={finalMenuPlacement}
          options={options}
          className={className}
          classNamePrefix={isSortedList ? 'select-customizable--show-values-as-list' : 'select-customizable'}
          defaultValue={defaultValue}
          value={value}
          placeholder={resolvedPlaceholder}
          inputId={resolvedInputId}
          aria-labelledby={label ? labelId : undefined}
          aria-label={resolvedAriaLabel}
          components={{
            DropdownIndicator,
            Control: AccessibleControl,
            Input: AccessibleInput,
            Menu: AccessibleMenu,
            Option: AccessibleOption,
            ...((isSortedList || showValuesAsText) && {
              MultiValue: MultiValueContainer,
              ValueContainer,
            }),
          }}
          noOptionsMessage={getNoOptionsMessage}
          screenReaderStatus={getScreenReaderStatus}
          isMulti={isMulti}
          onChange={onChange}
          name={name}
          menuPortalTarget={menuPortalTarget}
          formatGroupLabel={formatGroupLabel}
          hideSelectedOptions={hideSelectedOptions}
          formatOptionLabel={getOptionLabel}
          styles={selectStyles}
          isSearchable={finalIsSearchable}
          {...props}
          {...sharedProps}
        />
      )}
      {error && (
        <span className="select-customizable__error" id={errorId} role="alert">
          {error}
        </span>
      )}
    </>
  );
}

export default SelectCustomizable;
